"use client"

import Link from "next/link"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import { useLocale } from "next-intl"

interface HeaderPageProps {
  title: string
  breadcrumbs: {
    label: string
    href?: string
  }[]
}

export default function HeaderPage({ title, breadcrumbs }: HeaderPageProps) {
    const locale = useLocale()
  
  return (
    <div className="w-full border-b border-border/40 bg-gradient-to-br from-background via-background to-card/30 animate-slide-up relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <Image src="/page-header-bg-shape.png" alt="Background shape" fill className="object-cover" priority />
      </div>

      <div className="absolute inset-0 pointer-events-none">
        {/* Cloud 1 - Left side, slow animation */}
        <div className="absolute top-8 left-0 animate-cloud-1">
          <Image
            src="/page-header-shape-1.png"
            alt="Cloud with airplane"
            width={140}
            height={100}
            className="opacity-70"
          />
        </div>

        {/* Cloud 2 - Right side, medium speed animation */}
        <div className="absolute top-12 right-0 animate-cloud-2">
          <Image src="/page-header-shape-2.png" alt="Hot air balloon" width={120} height={120} className="opacity-70" />
        </div>

        {/* Cloud 3 - Left side, fast animation */}
        <div className="absolute top-6 left-1/4 animate-cloud-3">
          <Image src="/page-header-shape-1.png" alt="Cloud" width={100} height={80} className="opacity-50" />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 flex flex-col items-center relative z-10">
        {/* Breadcrumbs Navigation */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            className="mb-6 md:mb-8 flex items-center gap-1 sm:gap-2 text-sm sm:text-base text-muted-foreground animate-slide-up"
            style={{ animationDelay: "0.1s" }}
          >
            {breadcrumbs.map((item, index) => (
              <div key={index} className="flex items-center gap-1 sm:gap-2">
                {item.href ? (
                  <Link
                    href={item.href}
                    className="transition-colors hover:text-foreground hover:underline underline-offset-2 whitespace-nowrap"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="font-semibold text-foreground whitespace-nowrap">
                    {item.label}
                  </span>
                )}

                {/* Separator */}
                {index < breadcrumbs.length - 1 && (
                  locale === "ar" ? (
                    <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 opacity-50" />
                  ) : (
                    <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 opacity-50" />
                  )
                )}
              </div>
            ))}
          </nav>
        )}

        {/* Title with Elegant Highlight */}
        <div className="flex flex-col gap-2 animate-slide-up text-center" style={{ animationDelay: "0.2s" }}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-foreground leading-tight">
            <span className="relative inline-block">
              <span className="relative z-10">{title}</span>
              <span
                className="absolute bottom-1 left-0 right-0 h-2 sm:h-3 md:h-4 bg-primary/15 -rotate-1 rounded-sm -z-10"
                aria-hidden="true"
              />
            </span>
          </h1>
        </div>
      </div>
    </div>
  )
}
